/**
 * Referral attribution: capture `?aff=<code>` (affiliate) and `?ref=<code>` (user invite) from
 * any landing URL and remember them until the visitor signs up (90 days). The signup call sends
 * them, and the server ties the new account to the affiliate / referrer. Also counts a visit to
 * an affiliate link once per browser session.
 *
 * Everything is best-effort and wrapped in try/catch — a blocked storage must never break a page.
 */
const AFF_KEY = "rb_aff";
const REF_KEY = "rb_ref";
const CLICKED_KEY = "rb_aff_clicked";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[a-z0-9]{4,32}$/i;

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

type Stored = { code: string; at: number };

function save(key: string, code: string) {
  try {
    localStorage.setItem(key, JSON.stringify({ code: code.toLowerCase(), at: Date.now() }));
  } catch {
    /* storage blocked */
  }
}

function read(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Stored;
    if (!v?.code || Date.now() - (v.at || 0) > MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return v.code;
  } catch {
    return null;
  }
}

/** Run on any public page load: store codes from the URL and count an affiliate visit.
 *  Returns true when the address carried a valid ?aff= or ?ref= code. */
export function captureReferral(): boolean {
  if (typeof window === "undefined") return false;
  let found = false;
  try {
    const q = new URLSearchParams(window.location.search);
    const aff = (q.get("aff") || "").trim();
    const ref = (q.get("ref") || "").trim();
    if (aff && CODE_RE.test(aff)) {
      const firstTime = read(AFF_KEY) !== aff.toLowerCase();
      save(AFF_KEY, aff);
      countAffiliateVisit(aff, firstTime);
      found = true;
    }
    if (ref && CODE_RE.test(ref)) {
      save(REF_KEY, ref);
      found = true;
    }
  } catch {
    /* never break the page */
  }
  return found;
}

function countAffiliateVisit(code: string, firstTime: boolean) {
  try {
    const seen = sessionStorage.getItem(CLICKED_KEY);
    if (seen === code && !firstTime) return; // once per session per code
    sessionStorage.setItem(CLICKED_KEY, code);
    fetch(`${API_BASE}/api/affiliate/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

/** The stored codes to send with signup (empty strings when none). */
export function referralParams(): { aff?: string; ref?: string } {
  const out: { aff?: string; ref?: string } = {};
  const aff = read(AFF_KEY);
  const ref = read(REF_KEY);
  if (aff) out.aff = aff;
  if (ref) out.ref = ref;
  return out;
}

/** Clear after a successful signup so a shared device doesn't re-attribute later sign-ups. */
export function clearReferral() {
  try {
    localStorage.removeItem(AFF_KEY);
    localStorage.removeItem(REF_KEY);
  } catch {
    /* ignore */
  }
}
